import { z } from "zod";

export const MODES_FAIRE_VALOIR = {
  direct: "Faire-valoir direct",
  fermage: "Fermage",
  metayage: "Métayage",
  autre: "Autre situation",
} as const;

export const MODALITES_REGLEMENT_FONCIER = {
  nature: "En nature",
  especes: "En espèces",
  mixte: "Mixte",
} as const;

const dateContrat = z.string().date("Date invalide.").nullable();

/** Situation déclarée pour toute la PCV ; aucune règle fiscale n'en est déduite. */
export const faireValoirParcelleSchema = z
  .object({
    mode: z.enum(["direct", "fermage", "metayage", "autre"]),
    bailleur: z.string().trim().min(1).max(200).nullable(),
    date_debut: dateContrat,
    date_fin: dateContrat,
    modalite_reglement: z.enum(["nature", "especes", "mixte"]).nullable(),
    /** Part contractuelle du bailleur, indépendante du règlement, sans conversion en kilos. */
    part_bailleur_pct: z.number().finite().min(0).max(100).multipleOf(0.01).nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.date_debut && value.date_fin && value.date_fin < value.date_debut)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["date_fin"],
        message: "La fin du contrat doit suivre son début.",
      });
    if (
      value.mode === "direct" &&
      Object.entries(value).some(([key, val]) => key !== "mode" && val !== null)
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mode"],
        message: "Le faire-valoir direct ne comporte pas de bailleur ni de conditions de bail.",
      });
  });

export type FaireValoirParcelle = z.infer<typeof faireValoirParcelleSchema>;

/** Bloc atomique : absent = inchangé ; null = effacement explicite. */
export function colonnesFaireValoir(value: FaireValoirParcelle | null | undefined) {
  if (value === undefined) return {};
  return {
    mode_faire_valoir: value?.mode ?? null,
    bailleur_libelle: value?.bailleur ?? null,
    date_debut_contrat: value?.date_debut ?? null,
    date_fin_contrat: value?.date_fin ?? null,
    modalite_reglement_foncier: value?.modalite_reglement ?? null,
    part_bailleur_pct: value?.part_bailleur_pct?.toFixed(2) ?? null,
  };
}

export function lireFaireValoir(value: {
  mode_faire_valoir: string | null;
  bailleur_libelle: string | null;
  date_debut_contrat: string | null;
  date_fin_contrat: string | null;
  modalite_reglement_foncier: string | null;
  part_bailleur_pct: string | null;
}): FaireValoirParcelle | null {
  if (!value.mode_faire_valoir) return null;
  return faireValoirParcelleSchema.parse({
    mode: value.mode_faire_valoir,
    bailleur: value.bailleur_libelle,
    date_debut: value.date_debut_contrat,
    date_fin: value.date_fin_contrat,
    modalite_reglement: value.modalite_reglement_foncier,
    part_bailleur_pct: value.part_bailleur_pct === null ? null : Number(value.part_bailleur_pct),
  });
}
